/**
 * Maintenance request create/edit — one component for both `new` and
 * `:id/edit`, switched on whether `useParams().id` is present.
 *
 * `assigned_agent` and `status` are deliberately NOT on this form even though
 * the backend's write schema accepts them (maintenance/serializers.ts:138-148)
 * — reassigning an agent and marking a request COMPLETED go through the
 * dedicated `assign`/`complete` actions on the detail page instead, which are
 * manager-only and carry their own bespoke request bodies. Keeping this form
 * to title/description/category/priority/unit matches what every caller,
 * including a tenant filing their own request, is meant to fill in.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { parseApiError } from '../../lib/apiError';
import {
  createMaintenanceRequest,
  getMaintenanceRequest,
  updateMaintenanceRequest,
} from '../../api/endpoints/maintenance';
import { REQUEST_CATEGORIES, REQUEST_PRIORITIES } from '../../types/maintenance';
import type {
  MaintenanceRequestCreateRequest,
  RequestCategory,
  RequestPriority,
} from '../../types/maintenance';

const INITIAL_FORM = {
  unit: '',
  category: 'OTHER' as RequestCategory,
  priority: 'MEDIUM' as RequestPriority,
  title: '',
  description: '',
};

type FormState = typeof INITIAL_FORM;

function toPayload(form: FormState): MaintenanceRequestCreateRequest {
  return {
    unit: Number(form.unit),
    category: form.category,
    priority: form.priority,
    title: form.title,
    description: form.description,
  };
}

export function MaintenanceFormPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined;
  const requestId = Number(id);
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const existing = useQuery({
    queryKey: ['maintenance-request', requestId],
    queryFn: () => getMaintenanceRequest(requestId),
    enabled: isEdit && Number.isFinite(requestId),
  });

  useEffect(() => {
    if (existing.data === undefined) return;
    const request = existing.data;
    setForm({
      unit: String(request.unit),
      category: request.category,
      priority: request.priority,
      title: request.title,
      description: request.description,
    });
  }, [existing.data]);

  function update(field: keyof FormState, value: string): void {
    setForm((previous) => ({ ...previous, [field]: value }));
  }

  const save = useMutation({
    mutationFn: () =>
      isEdit
        ? updateMaintenanceRequest(requestId, toPayload(form))
        : createMaintenanceRequest(toPayload(form)),
    onSuccess: (request) => navigate(isEdit ? '..' : `../${request.id}`, { replace: true }),
    onError: (caught) => {
      const parsed = parseApiError(caught);
      setError(parsed.message);
      setFieldErrors(parsed.fieldErrors);
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    save.mutate();
  }

  function fieldError(field: string): string | undefined {
    return fieldErrors[field]?.[0];
  }

  if (isEdit && existing.isPending) return <p>Loading…</p>;
  if (isEdit && existing.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(existing.error).message}
      </p>
    );
  }

  return (
    <section>
      <h2>{isEdit ? 'Edit maintenance request' : 'File a maintenance request'}</h2>

      <form onSubmit={handleSubmit} noValidate>
        {error !== null && (
          <p role="alert" className="form__error">
            {error}
          </p>
        )}

        <label htmlFor="maintenance-unit">Unit id</label>
        <input
          id="maintenance-unit"
          type="number"
          required
          value={form.unit}
          onChange={(event) => update('unit', event.target.value)}
        />
        {fieldError('unit') !== undefined && <p className="form__field-error">{fieldError('unit')}</p>}

        <label htmlFor="maintenance-category">Category</label>
        <select
          id="maintenance-category"
          value={form.category}
          onChange={(event) => update('category', event.target.value)}
        >
          {REQUEST_CATEGORIES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>

        <label htmlFor="maintenance-priority">Priority</label>
        <select
          id="maintenance-priority"
          value={form.priority}
          onChange={(event) => update('priority', event.target.value)}
        >
          {REQUEST_PRIORITIES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>

        <label htmlFor="maintenance-title">Title</label>
        <input
          id="maintenance-title"
          required
          value={form.title}
          onChange={(event) => update('title', event.target.value)}
        />
        {fieldError('title') !== undefined && <p className="form__field-error">{fieldError('title')}</p>}

        <label htmlFor="maintenance-description">Description</label>
        <textarea
          id="maintenance-description"
          required
          value={form.description}
          onChange={(event) => update('description', event.target.value)}
        />
        {fieldError('description') !== undefined && (
          <p className="form__field-error">{fieldError('description')}</p>
        )}

        <button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </form>
    </section>
  );
}

export default MaintenanceFormPage;
